import { onBeforeMount, onBeforeUnmount } from 'vue'
import type { Elemental } from '../types/elemental'
import type { AditonalFormData } from '../utils/elementalValidationsFactory'

interface DragData<T> { index: number; data: T }

export interface RenderCustomEvent {
  'accordeon-toggle': {
    id: string
    value: boolean
    groupId?: string
  }
  'scroll-to': {
    id: string
  }
  'form:add-prompt-to-list': {
    element: Elemental
  }
  'form:update-prompt-on-list': {
    elementals: Elemental[]
  }
  'form:auto-save:changed': {
    id: string
    field: string
    value: unknown
  }
  'form:auto-save:change': {
    id: string
    field: string
    value: unknown
  }
  'elemental:created': {
    element: Elemental
  }
  'elemental:updated': {
    element: Elemental
  }
  'elemental:delete': {
    elementId: string
  }
  'elemental:form-data-change': {
    element: AditonalFormData & Elemental
    origin: 'form' | 'cell-editor'
  }
  'table:update-row-data': {
    rowId: string
    colId: string
    cellId: string
    value: string
  }
  'table:update-column-data': {
    colId: string
    value: string
  }
  'prompt-use:change': Record<string, string>
  'dropzone:dragenter': {
    id: string
    data: DragData<any> | null
  }
  'dropzone:dragleave': {
    id: string
    data: DragData<any> | null
  }
}
export function customEventListem<T extends keyof RenderCustomEvent>(event: T,
  callback: (event: RenderCustomEvent[T]) => void) {
  const handler = (event: unknown) => {
    const detail = (event as CustomEvent).detail
    callback(detail)
  }

  window.addEventListener(event, handler)
  return () => {
    window.removeEventListener(event, handler)
  }
}

export const useCustomEventListener = <T extends keyof RenderCustomEvent>(
  event: T,
  callback: (event: RenderCustomEvent[T]) => void,
) => {
  const handler = (event: unknown) => {
    const detail = (event as CustomEvent).detail
    callback(detail)
  }

  onBeforeMount(() => {
    window.addEventListener(event, handler)
  })

  onBeforeUnmount(() => {
    window.removeEventListener(event, handler)
  })

  return () => {
    window.removeEventListener(event, handler)
  }
}

export const useDispatchCustomEvent = <T extends keyof RenderCustomEvent>(
  event: T,
  data: RenderCustomEvent[T],
) => {
  window.dispatchEvent(new CustomEvent(event, { detail: data }))
}
